'use client'

import * as React from 'react'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { CalendarIcon, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn, formatDisplayDate } from '@/utils/common'
import { DATE_PICKER_FORMAT } from '@/utils/constants'

// Enable custom parse format plugin for dayjs
dayjs.extend(customParseFormat)

// const DATE_PICKER_FORMAT_VALIDATION_MM_DD_YYYY = 'MM-DD-YYYY'
// const DATE_PICKER_FORMAT_VALIDATION_M_D_YYYY = 'M-D-YYYY'

/**
 * Normalizes a date by setting hours, minutes, seconds, and milliseconds to 0
 * @param date - The date to normalize
 * @returns A new Date object with time set to midnight
 */
const normalizeDate = (date: Date): Date => {
  const normalized = new Date(date)
  normalized.setHours(0, 0, 0, 0)
  return normalized
}

/**
 * Gets today's date normalized to midnight
 * @returns Today's date with time set to midnight
 */
const getToday = (): Date => {
  return normalizeDate(new Date())
}

/**
 * Validates and normalizes a date string.
 *
 * Supported input formats (with any of '/', '-', '.' as separators):
 * - MM/DD/YYYY
 * - M/D/YYYY
 * - M/DD/YYYY
 * - MM/D/YYYY
 *
 * If valid, returns a canonical string in MM/DD/YYYY format.
 * If invalid, returns an empty string.
 *
 * @param dateString - The date string to validate
 * @returns Canonical date string in MM/DD/YYYY format or empty string if invalid
 */
const isValidDate = (dateString: string): string => {
  if (!dateString || dateString.trim() === '') {
    return ''
  }

  // Normalize separators to '/' to support -, ., /
  const normalizedInput = dateString.replace(/[-.]/g, '/') // NOSONAR

  // Try the common month/day formats with strict parsing
  const parseFormats = [
    'MM/DD/YYYY',
    'M/D/YYYY',
    'M/DD/YYYY',
    'MM/D/YYYY',
  ] as const

  let parsedDate = dayjs(normalizedInput, parseFormats[0], true)
  for (let i = 1; i < parseFormats.length && !parsedDate.isValid(); i++) {
    parsedDate = dayjs(normalizedInput, parseFormats[i], true)
  }

  // Check if the date is valid and matches the format exactly
  if (!parsedDate.isValid()) {
    return ''
  }

  // Return canonical MM/DD/YYYY formatted string
  return parsedDate.format(DATE_PICKER_FORMAT)
}

type ClearButtonProps = {
  onClear: (e: React.MouseEvent) => void
}

const ClearButton: React.FC<ClearButtonProps> = ({ onClear }) => (
  <Button
    type="button"
    variant="text"
    onClick={onClear}
    onPointerDown={(e) => {
      e.stopPropagation()
    }}
    onMouseDown={(e) => {
      e.stopPropagation()
    }}
    className="flex items-center justify-center rounded-sm p-0.5 gap-0"
    aria-label="Clear date"
    leadingIcon={<X className="size-3.5 text-gray-500" />}
  />
)

export interface DatePickerProps {
  /**
   * Placeholder text shown when no date is selected
   */
  placeholder?: string
  /**
   * Callback function called when the date changes
   * @param date - The selected date or undefined if cleared
   */
  onDateChange?: (date: Date | undefined) => void
  /**
   * Icon component to display in the picker button
   * @default CalendarIcon
   */
  pickerIcon?: React.ReactNode
  /**
   * The currently selected date
   */
  value?: Date
  /**
   * Additional className for the container
   */
  className?: string
  /**
   * Whether the date picker should take the full width of the container
   */
  fullWidth?: boolean
  /**
   * ID for the date picker (useful for form associations)
   */
  id?: string
  /**
   * Whether the date picker is disabled
   */
  disabled?: boolean
  /**
   * Minimum date that can be selected (dates before this will be disabled)
   */
  fromDate?: Date
  /**
   * Maximum date that can be selected (dates after this will be disabled)
   */
  toDate?: Date
  /**
   * Whether to show a clear button when a date is selected
   * @default true
   */
  clearable?: boolean
  /**
   * If true, disables all past dates and only allows selecting future dates (from today onwards)
   * When enabled, automatically sets fromDate to today
   * @default false
   */
  onlyFutureDate?: boolean
  /**
   * If true, allows users to type dates directly into an input field
   * When enabled, the component will validate the input and update the date if valid
   * @default false
   */
  allowInput?: boolean
}

/**
 * DatePicker Component
 *
 * A date picker component built using shadcn/ui Calendar and Popover components.
 * Provides a clean interface for selecting dates with customizable label, placeholder, and icon.
 *
 * Features:
 * - Clear button to remove selected date (when clearable is true)
 * - Customizable placeholder and icon
 * - Full width option
 * - Disabled state
 * - Date range restrictions (fromDate, toDate)
 * - Option to disable past dates (onlyFutureDate)
 * - Input mode: allows typing dates directly (when allowInput is true)
 * - Icon-only popover trigger: calendar opens when clicking the icon
 *
 * @param clearable - Whether to show a clear button when a date is selected (default: true)
 * @param onlyFutureDate - If true, disables all past dates and only allows future dates (default: false)
 * @param allowInput - If true, allows users to type dates directly into an input field (default: false)
 * @example
 * ```tsx
 * <DatePicker
 *   placeholder="Select date"
 *   onDateChange={(date) => console.log(date)}
 *   clearable
 *   onlyFutureDate
 *   allowInput
 * />
 * ```
 */
export const DatePicker = ({
  placeholder = '',
  onDateChange,
  pickerIcon,
  value,
  className,
  fullWidth,
  id,
  disabled = false,
  clearable = true,
  onlyFutureDate = false,
  fromDate,
  toDate,
  allowInput = false,
}: DatePickerProps) => {
  const [open, setOpen] = React.useState(false)
  const [date, setDate] = React.useState<Date | undefined>(value)
  const [month, setMonth] = React.useState<Date | undefined>(value)
  const [inputValue, setInputValue] = React.useState<string>(
    value ? formatDisplayDate(value, DATE_PICKER_FORMAT) : ''
  )

  // Sync internal state with external value prop
  React.useEffect(() => {
    setDate(value)
    setMonth(value)
    if (value) {
      setInputValue(formatDisplayDate(value, DATE_PICKER_FORMAT))
    } else {
      setInputValue('')
    }
  }, [value])

  React.useEffect(() => {
    // If the month is not the same as the value, set the month to the value
    if (open && month && value && month.getTime() !== value.getTime()) {
      setMonth(value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Calculate the minimum date (fromDate prop or today if disablePast is true)
  const minDate = React.useMemo(() => {
    if (onlyFutureDate) {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return today
    }
    return fromDate
  }, [onlyFutureDate, fromDate])

  /**
   * Checks if a date passes all restrictions (onlyFutureDate, minDate, toDate)
   * @param date - The date to check
   * @returns true if the date passes all restrictions, false otherwise
   */
  const passesDateRestrictions = React.useCallback(
    (date: Date): boolean => {
      const normalizedDate = normalizeDate(date)

      // Check onlyFutureDate restriction
      if (onlyFutureDate) {
        const today = getToday()
        if (normalizedDate < today) {
          return false
        }
      }

      // Check fromDate restriction
      if (minDate) {
        const min = normalizeDate(minDate)
        if (normalizedDate < min) {
          return false
        }
      }

      // Check toDate restriction
      if (toDate) {
        const max = normalizeDate(toDate)
        if (normalizedDate > max) {
          return false
        }
      }

      return true
    },
    [onlyFutureDate, minDate, toDate]
  )

  /**
   * Sets the date to today and updates all related state
   */
  const setTodayDate = React.useCallback(() => {
    const today = getToday()
    const todayString = formatDisplayDate(today, DATE_PICKER_FORMAT)
    setInputValue(todayString)
    setDate(today)
    onDateChange?.(today)
  }, [onDateChange])

  /**
   * Handles input change events
   * Only stores the raw string value without validation
   */
  const handleInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      setInputValue(newValue)
    },
    []
  )

  /**
   * Handles input blur events
   * Validates and normalizes the input value
   */
  const handleInputBlur = React.useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const rawValue = e.target.value
      const trimmedValue = rawValue.trim()

      // If empty -> treat as null date
      if (!trimmedValue || trimmedValue === '') {
        setInputValue('')
        setDate(undefined)
        onDateChange?.(undefined)
        return
      }

      // Check if valid date or not
      const formattedDate = isValidDate(trimmedValue)
      if (!formattedDate) {
        // If not valid, set date to today date, and reformat input text with today date
        setTodayDate()
        return
      }

      // If valid, save canonical date in state
      const parsedDate = dayjs(formattedDate, DATE_PICKER_FORMAT, true).toDate()

      // Check date restrictions
      if (passesDateRestrictions(parsedDate)) {
        // Reformat input text to canonical string and overwrite the input value
        const canonicalString = formatDisplayDate(
          parsedDate,
          DATE_PICKER_FORMAT
        )
        setInputValue(canonicalString)
        setDate(parsedDate)
        onDateChange?.(parsedDate)
      } else {
        // If date doesn't pass restrictions, set to today
        setTodayDate()
      }
    },
    [onDateChange, passesDateRestrictions, setTodayDate]
  )

  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (selectedDate && onlyFutureDate) {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const selected = new Date(selectedDate)
      selected.setHours(0, 0, 0, 0)

      // Only allow dates that are today or in the future
      if (selected < today) {
        return // Don't allow past dates
      }
    }

    setDate(selectedDate)
    // Update input value when date is selected from calendar
    if (allowInput) {
      setInputValue(
        selectedDate ? formatDisplayDate(selectedDate, DATE_PICKER_FORMAT) : ''
      )
    }
    onDateChange?.(selectedDate)
    setOpen(false)
  }

  const handleClear = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      e.preventDefault()
      setDate(undefined)
      if (allowInput) setInputValue('')
      onDateChange?.(undefined)
    },
    [onDateChange, allowInput]
  )

  const defaultIcon = React.useMemo(
    () => <CalendarIcon className="size-4" />,
    []
  )
  const icon = React.useMemo(
    () => pickerIcon ?? defaultIcon,
    [defaultIcon, pickerIcon]
  )
  const showClearButton = React.useMemo(
    () => clearable && !!date && !disabled,
    [clearable, date, disabled]
  )

  return (
    <div
      className={cn(
        'flex flex-col gap-3',
        fullWidth ? 'w-full' : 'max-w-[320px]',
        className
      )}
    >
      <Popover open={open} onOpenChange={setOpen}>
        {allowInput ? (
          // Input mode: render Input component with icon trigger
          <div className="relative">
            <Input
              id={id}
              disabled={disabled}
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onKeyDown={(e) => {
                switch (e.key) {
                  case 'ArrowDown':
                    e.preventDefault()
                    setOpen(true)
                    break

                  case 'Enter':
                    e.preventDefault()
                    handleInputBlur({
                      target: { value: inputValue },
                    } as React.FocusEvent<HTMLInputElement>)
                    break

                  default:
                    break
                }
              }}
              placeholder={placeholder || DATE_PICKER_FORMAT}
              className={cn(
                'text-gray-900',
                fullWidth ? 'w-full' : 'max-w-[320px]',
                showClearButton || icon ? 'pr-20' : 'pr-10'
              )}
            />
            {/* Custom trailing icons positioned absolutely */}
            <div className="absolute inset-y-0 right-0 flex items-center gap-1 pr-1.5 z-10">
              {showClearButton && <ClearButton onClear={handleClear} />}
              <PopoverTrigger asChild disabled={disabled}>
                <Button
                  type="button"
                  variant="text"
                  className="p-1.5 gap-0 text-gray-400"
                  aria-label="Open calendar"
                  leadingIcon={icon}
                />
              </PopoverTrigger>
            </div>
          </div>
        ) : (
          // Button mode: render Button component with trigger
          <PopoverTrigger asChild>
            <Button
              variant="outlined"
              color="primary"
              id={id}
              disabled={disabled}
              className={cn(
                '',
                'flex flex-row-reverse min-h-10 items-center justify-between gap-2 rounded-lg border border-solid border-gray-light-300 bg-white px-3 py-2 text-sm transition-colors',
                'hover:border-blue-400 hover:bg-white',
                'focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500',
                date ? 'text-gray-900' : 'text-gray-400',
                disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                fullWidth ? 'w-full' : 'max-w-[320px]',
                'text-left'
              )}
              leadingIcon={
                <div className="flex items-center gap-1">
                  {showClearButton && <ClearButton onClear={handleClear} />}
                  {icon}
                </div>
              }
            >
              {date ? formatDisplayDate(date, DATE_PICKER_FORMAT) : placeholder}
            </Button>
          </PopoverTrigger>
        )}
        <PopoverContent
          className="w-auto overflow-hidden p-0"
          align="end"
          sideOffset={10}
        >
          <Calendar
            mode="single"
            captionLayout="dropdown"
            month={month}
            onMonthChange={setMonth}
            selected={value}
            onSelect={handleDateSelect}
            disabled={[
              (date) => {
                if (!minDate) return false
                return date < minDate
              },
              (date) => {
                if (!toDate) return false
                return date > toDate
              },
            ]}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}
